/* eslint-disable @typescript-eslint/no-explicit-any */
import { Dictionary } from "@/utils/Dictionary";
import { useEditor, useNode } from "@craftjs/core";
import clsx from "clsx";
import { useEffect, useRef } from "react";
import ContentEditable from "react-contenteditable";
import Settings from "./Settings";
import Toolbar from "./Toolbar";
import { sanitizeClient } from "./sanitizeBlock";

const convertTableToBlockData = (
    table: HTMLTableElement
): { content: Array<Array<string>>; hasHeaderRow: "yes" | "no" } => {
    const rows = table.querySelectorAll("tr");
    const tableData: Array<Array<string>> = [];
    const containsHeaderRow = rows[0].querySelectorAll("th").length > 0;

    let columnCount = 0;
    for (let j = 0; j < rows.length; j++) {
        const row = rows[j];
        const cells = row.querySelectorAll("td,th");
        const rowData: Array<string> = [];
        if (cells.length > columnCount) columnCount = cells.length;

        for (let k = 0; k < cells.length; k++) {
            const cell = cells[k];
            rowData.push(cell.innerHTML);
        }

        tableData.push(rowData);
    }

    for (let j = 0; j < tableData.length; j++) {
        const rowData = tableData[j];
        if (rowData.length < columnCount) {
            for (let k = rowData.length; k < columnCount; k++) {
                if (j === 0) rowData.unshift("");
                else rowData.push("");
            }
        }
    }

    return { content: tableData, hasHeaderRow: containsHeaderRow ? "yes" : "no" };
};

interface TableProps {
    content: Array<Array<string>>;
    columnWidths: Dictionary<number>;
    hasHeaderRow: "yes" | "no";
}

const defaultProps: TableProps = {
    content: [
        ["", ""],
        ["", ""],
    ],
    columnWidths: {},
    hasHeaderRow: "yes",
};

const TableBlock = ({
    content = defaultProps.content,
    hasHeaderRow = defaultProps.hasHeaderRow,
    columnWidths = defaultProps.columnWidths,
}: Partial<TableProps>) => {
    const tableRef = useRef<HTMLTableElement>(null);

    const {
        parentNodeId,
        connectors: { connect },
        actions: { setProp },
    } = useNode((node) => ({
        parentNodeId: node.data.parent,
    }));

    const {
        actions: {
            history: { throttle },
        },
        query: { parseReactElement },
        enabled,
    } = useEditor((state) => ({ enabled: state.options.enabled }));

    useEffect(() => {
        if (!tableRef.current) return;

        const tableContainer = tableRef.current;
        tableContainer.addEventListener("paste", onPaste);

        return () => {
            tableContainer.removeEventListener("paste", onPaste);
        };

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onPaste = async (event: ClipboardEvent) => {
        event.preventDefault();

        const selection = document.getSelection();
        if (!selection || !selection.focusNode) return;

        const range = selection.getRangeAt(0).cloneRange();
        if (!range) return;

        const dataTransfer = event.clipboardData;
        if (!dataTransfer) return;

        const htmlData = dataTransfer.getData("text/html");
        if (htmlData) {
            const sanitizedHtmlData = sanitizeClient(htmlData);
            const template = document.createElement("template");
            template.innerHTML = sanitizedHtmlData.trim();

            if (template.content.children.length === 0) return;

            const tables = template.content.querySelectorAll("table");
            const { addNodeTree } = throttle(1000);

            if (tables.length === 0) {
                const selection = window.getSelection();
                if (!selection?.rangeCount) return;

                selection.deleteFromDocument();
                selection.getRangeAt(0).insertNode(template.content);
                selection.collapseToEnd();
            } else {
                for (let i = 0; i < tables.length; i++) {
                    const table = tables[i];
                    const data = convertTableToBlockData(table);

                    if (i === 0) {
                        setProp((props: any) => {
                            props.content = data.content;
                            props.hasHeaderRow = data.hasHeaderRow;
                        });
                    } else {
                        addNodeTree(
                            parseReactElement(
                                <TableBlock content={data.content} hasHeaderRow={data.hasHeaderRow} />
                            ).toNodeTree(),
                            parentNodeId!
                        );
                    }
                }
            }
        } else {
            const plainTextData = dataTransfer.getData("text/plain");
            const selection = window.getSelection();
            if (!selection?.rangeCount) return;

            const textNode = document.createTextNode(plainTextData);

            selection.deleteFromDocument();
            selection.getRangeAt(0).insertNode(textNode);
            selection.collapseToEnd();
        }
    };

    const renderTableHead = () => {
        if (hasHeaderRow === "no") return null;

        return (
            <thead className="bg-primary-500 text-white font-bold">
                <tr>
                    {content[0].map((cell, index) => {
                        const hasWidth = columnWidths[index] ? `${columnWidths[index]}px` : "auto";
                        return (
                            <th
                                key={`0-${index}`}
                                data-row-id={0}
                                data-column-id={index}
                                style={{ width: hasWidth }}
                                scope="col"
                                className={clsx([
                                    "py-3 px-2.5 text-center",
                                    "border-primary-600 border-t border-l",
                                    "first:rounded-tl-lg last:rounded-tr-lg last:border-r",
                                ])}
                            >
                                <ContentEditable
                                    html={cell || ""}
                                    disabled={!enabled}
                                    onChange={(e) => {
                                        setProp((props: any) => (props.content[0][index] = e.target.value), 500);
                                    }}
                                    tagName="span"
                                    style={{ display: "block", width: "100%", minHeight: "1rem" }}
                                />
                            </th>
                        );
                    })}
                </tr>
            </thead>
        );
    };

    const renderTableBody = () => {
        let startIndex = 0;
        if (hasHeaderRow === "yes") startIndex = 1;

        return (
            <tbody className="last:[&>tr:last-child>td:first-child]:rounded-bl-lg last:[&>tr:last-child>td:last-child]:rounded-br-lg">
                {content.slice(startIndex).map((row, rowIndex) => (
                    <tr
                        key={rowIndex}
                        className={clsx([hasHeaderRow === "no" ? "odd:bg-[#F7FAFF]" : "even:bg-[#F7FAFF]"])}
                    >
                        {row.map((cell, cellIndex) => (
                            <td
                                key={cellIndex}
                                scope="row"
                                data-row-id={rowIndex + startIndex}
                                data-column-id={cellIndex}
                                className={clsx([
                                    "py-3 px-2.5 border-l border-b border-[#E5E5E5] font-medium last:border-r",
                                    "break-normal border-b",
                                ])}
                            >
                                <ContentEditable
                                    html={cell || ""}
                                    disabled={!enabled}
                                    onChange={(e) => {
                                        setProp(
                                            (props: any) =>
                                                (props.content[rowIndex + startIndex][cellIndex] = e.target.value),
                                            500
                                        );
                                    }}
                                    tagName="span"
                                    style={{ display: "block", width: "100%", minHeight: "1rem" }}
                                />
                            </td>
                        ))}
                    </tr>
                ))}
            </tbody>
        );
    };

    return (
        <div ref={(ref) => connect(ref as HTMLElement)}>
            <table
                ref={tableRef}
                className="group w-full divide-y divide-gray-300 rounded-lg border-separate border-spacing-0 text-[15px]/[20px] table-fixed"
            >
                {renderTableHead()}
                {renderTableBody()}
            </table>
        </div>
    );
};

export default TableBlock;

TableBlock.craft = {
    displayName: "Table",
    props: defaultProps,
    related: {
        settings: Settings,
        toolbar: Toolbar,
    },
    rules: {
        canDrag: () => true,
        canMoveOut: () => false,
        canDrop: (targetNode: any) =>
            targetNode.data.name === "Grid" ||
            targetNode.data.name === "Div" ||
            targetNode.data.name === "PageContentContainer",
    },
};
